import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import "./ResearchPaperBuilder.css";

const managerRoles = ["Admin", "Archivist", "Researcher", "Secretary"];

function emptyPaper() {
    return {
        title: `Connaught 361 Research Paper ${new Date().getFullYear()}`,
        volume: "1",
        issue_number: "1",
        issue_date: "",
        circulation_note: "For Private Circulation",
        editor_name: "",
        contact_email: "connaught361archive@gmail.com",
    };
}

function formatDateTime(value) {
    if (!value) return "Not built";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "Not built";
    return new Intl.DateTimeFormat("en-ZA", {
        dateStyle: "medium",
        timeStyle: "short",
    }).format(date);
}

function paperNeedsBuild(paper) {
    if (!paper?.pdf_path || !paper?.pdf_built_at) return true;
    const built = new Date(paper.pdf_built_at).getTime();
    const updated = new Date(paper.updated_at).getTime();
    return Number.isFinite(updated) && Number.isFinite(built) && updated > built + 5000;
}

export default function ResearchPaperBuilder({ user }) {
    const [papers, setPapers] = useState([]);
    const [selectedId, setSelectedId] = useState("");
    const [paper, setPaper] = useState(null);
    const [activeSectionKey, setActiveSectionKey] = useState("");
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [building, setBuilding] = useState(false);
    const [creating, setCreating] = useState(false);
    const [message, setMessage] = useState("");
    const [error, setError] = useState("");

    const canManage = managerRoles.includes(user?.role);
    const activeSection = useMemo(
        () => paper?.sections?.find((section) => section.key === activeSectionKey) || paper?.sections?.[0],
        [paper, activeSectionKey]
    );

    const loadPapers = useCallback(async (selectId = "") => {
        setLoading(true);
        setError("");
        try {
            const response = await axios.get("/api/research-papers", { withCredentials: true });
            const nextPapers = response.data || [];
            setPapers(nextPapers);
            const nextSelected = selectId || nextPapers[0]?.id || "";
            setSelectedId(nextSelected);
            setPaper(nextPapers.find((item) => item.id === nextSelected) || nextPapers[0] || null);
            setActiveSectionKey((current) => current || nextPapers[0]?.sections?.[0]?.key || "");
        } catch (loadError) {
            setError(loadError.response?.data?.error || "Could not load research papers.");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadPapers();
    }, [loadPapers]);

    function selectPaper(nextPaper) {
        setSelectedId(nextPaper.id);
        setPaper(nextPaper);
        setActiveSectionKey(nextPaper.sections?.[0]?.key || "");
        setMessage("");
        setError("");
    }

    function updatePaperField(name, value) {
        setPaper((current) => ({ ...current, [name]: value }));
    }

    function updateSection(key, patch) {
        setPaper((current) => ({
            ...current,
            sections: current.sections.map((section) => (
                section.key === key ? { ...section, ...patch } : section
            )),
        }));
    }

    async function createPaper() {
        setCreating(true);
        setError("");
        setMessage("");
        try {
            const response = await axios.post("/api/research-papers", emptyPaper(), { withCredentials: true });
            setPapers((current) => [response.data, ...current]);
            selectPaper(response.data);
            setMessage("Research paper created.");
        } catch (createError) {
            setError(createError.response?.data?.error || "Could not create research paper.");
        } finally {
            setCreating(false);
        }
    }

    async function savePaper() {
        if (!paper) return;
        setSaving(true);
        setError("");
        setMessage("");
        try {
            const response = await axios.put(`/api/research-papers/${paper.id}`, paper, { withCredentials: true });
            setPaper(response.data);
            setPapers((current) => current.map((item) => item.id === response.data.id ? response.data : item));
            setMessage("Research paper saved.");
        } catch (saveError) {
            setError(saveError.response?.data?.error || "Could not save research paper.");
        } finally {
            setSaving(false);
        }
    }

    async function buildPdf() {
        if (!paper) return;
        setBuilding(true);
        setError("");
        setMessage("");
        try {
            const saved = await axios.put(`/api/research-papers/${paper.id}`, paper, { withCredentials: true });
            const built = await axios.post(`/api/research-papers/${paper.id}/build`, {}, { withCredentials: true });
            const nextPaper = { ...saved.data, ...built.data };
            setPaper(nextPaper);
            setPapers((current) => current.map((item) => item.id === nextPaper.id ? nextPaper : item));
            setMessage("PDF built successfully.");
        } catch (buildError) {
            const details = buildError.response?.data?.details;
            setError(`${buildError.response?.data?.error || "Could not build PDF."}${details ? ` ${details.slice(0, 260)}` : ""}`);
        } finally {
            setBuilding(false);
        }
    }

    async function deletePaper() {
        if (!paper) return;
        const confirmed = window.confirm(`Delete "${paper.title}"?`);
        if (!confirmed) return;
        setSaving(true);
        setError("");
        try {
            await axios.delete(`/api/research-papers/${paper.id}`, { withCredentials: true });
            const remaining = papers.filter((item) => item.id !== paper.id);
            setPapers(remaining);
            selectPaper(remaining[0] || { id: "", sections: [] });
            if (!remaining.length) setPaper(null);
            setMessage("Research paper deleted.");
        } catch (deleteError) {
            setError(deleteError.response?.data?.error || "Could not delete research paper.");
        } finally {
            setSaving(false);
        }
    }

    if (loading) {
        return <div className="paper-builder-shell">Loading research paper builder...</div>;
    }

    return (
        <div className="paper-builder-shell">
            <div className="paper-builder-header">
                <div>
                    <h1>Research Papers</h1>
                    <p>Build Connaught 361 research issues from structured LaTeX sections.</p>
                </div>
                {canManage && (
                    <button type="button" onClick={createPaper} disabled={creating}>
                        {creating ? "Creating..." : "New Paper"}
                    </button>
                )}
            </div>

            {message && <div className="paper-builder-message success">{message}</div>}
            {error && <div className="paper-builder-message error">{error}</div>}

            <div className="paper-builder-grid">
                <aside className="paper-list-panel">
                    <h2>Issues</h2>
                    {papers.length === 0 ? (
                        <p className="paper-empty">No research papers yet.</p>
                    ) : (
                        papers.map((item) => (
                            <button
                                type="button"
                                key={item.id}
                                className={item.id === selectedId ? "paper-list-item active" : "paper-list-item"}
                                onClick={() => selectPaper(item)}
                            >
                                <strong>{item.title}</strong>
                                <span>Vol. {item.volume} No. {item.issue_number}</span>
                            </button>
                        ))
                    )}
                </aside>

                {paper ? (
                    <section className="paper-editor-panel">
                        <div className="paper-meta-grid">
                            <label>
                                Title
                                <input value={paper.title || ""} onChange={(event) => updatePaperField("title", event.target.value)} disabled={!canManage} />
                            </label>
                            <label>
                                Volume
                                <input value={paper.volume || ""} onChange={(event) => updatePaperField("volume", event.target.value)} disabled={!canManage} />
                            </label>
                            <label>
                                Number
                                <input value={paper.issue_number || ""} onChange={(event) => updatePaperField("issue_number", event.target.value)} disabled={!canManage} />
                            </label>
                            <label>
                                Issue Date
                                <input value={paper.issue_date || ""} onChange={(event) => updatePaperField("issue_date", event.target.value)} disabled={!canManage} />
                            </label>
                            <label>
                                Circulation
                                <input value={paper.circulation_note || ""} onChange={(event) => updatePaperField("circulation_note", event.target.value)} disabled={!canManage} />
                            </label>
                            <label>
                                Editor
                                <input value={paper.editor_name || ""} onChange={(event) => updatePaperField("editor_name", event.target.value)} disabled={!canManage} />
                            </label>
                            <label>
                                Contact Email
                                <input value={paper.contact_email || ""} onChange={(event) => updatePaperField("contact_email", event.target.value)} disabled={!canManage} />
                            </label>
                        </div>

                        <div className="paper-toolbar">
                            <span>PDF built: {formatDateTime(paper.pdf_built_at)}</span>
                            {paperNeedsBuild(paper) && <strong>Rebuild recommended</strong>}
                            <div>
                                {paper.pdf_path && <a href={`/api/research-papers/${paper.id}/pdf`} target="_blank" rel="noreferrer">PDF</a>}
                                {paper.tex_path && <a href={`/api/research-papers/${paper.id}/source`}>TeX</a>}
                                {canManage && <button type="button" onClick={savePaper} disabled={saving || building}>{saving ? "Saving..." : "Save"}</button>}
                                {canManage && <button type="button" onClick={buildPdf} disabled={saving || building}>{building ? "Building..." : "Build PDF"}</button>}
                                {canManage && <button type="button" className="danger" onClick={deletePaper} disabled={saving || building}>Delete</button>}
                            </div>
                        </div>

                        <div className="section-workspace">
                            <nav className="section-tabs" aria-label="Research paper sections">
                                {(paper.sections || []).map((section) => (
                                    <button
                                        type="button"
                                        key={section.key}
                                        className={section.key === activeSection?.key ? "active" : ""}
                                        onClick={() => setActiveSectionKey(section.key)}
                                    >
                                        {section.title}
                                    </button>
                                ))}
                            </nav>

                            {activeSection && (
                                <div className="section-editor">
                                    <label className="section-enabled">
                                        <input
                                            type="checkbox"
                                            checked={activeSection.enabled !== false}
                                            onChange={(event) => updateSection(activeSection.key, { enabled: event.target.checked })}
                                            disabled={!canManage}
                                        />
                                        Include section
                                    </label>
                                    <textarea
                                        value={activeSection.content || ""}
                                        onChange={(event) => updateSection(activeSection.key, { content: event.target.value })}
                                        spellCheck="false"
                                        disabled={!canManage}
                                    />
                                </div>
                            )}
                        </div>
                    </section>
                ) : (
                    <section className="paper-editor-panel empty">
                        <h2>No Paper Selected</h2>
                        <p>Create a paper to start from the Connaught 361 LaTeX template.</p>
                    </section>
                )}
            </div>
        </div>
    );
}
